// workflow.js — the quote lifecycle in one place: which statuses belong to
// which Quote Log tab, which manual changes are allowed (Automotive takes a
// deposit instead of a GoodSign contract), loss reasons, the dashboard's
// conversion figures, the latest status note, and job-date clashes.
// Pure functions of the Quote Log rows, so they can be tested on their own.
import { getJobDates } from "./quoteLogger";

// Same test as quoteUi's isTestQuote (column Y), kept here so this module
// has no UI imports.
const isTestQuote = q => q?.["Test Quote"] === "Yes";

export const DEPOSIT_PENDING = "Deposit pending";
export const DEPOSIT_PAID = "Deposit paid";
export const LOST = "Lost";

// Quote Log tabs. "Rejected" is the old name for Lost (rows not yet
// converted); Expired and Withdrawn are listed in case they're ever used.
export const ACTIVE_STATUSES = ["Draft", "Sent", "Amended", "Accepted", DEPOSIT_PENDING, DEPOSIT_PAID, "Contracted", "Invoiced"];
export const COMPLETED_STATUSES = ["Complete"];
export const CLOSED_STATUSES = [LOST, "Rejected", "Expired", "Withdrawn"];
export const tabOfStatus = s => CLOSED_STATUSES.includes(s) ? "closed" : COMPLETED_STATUSES.includes(s) ? "completed" : "active";

// A booked job: on the calendar, checked for dry ice orders, counted under Jobs.
export const JOB_STATUSES = ["Accepted", DEPOSIT_PENDING, DEPOSIT_PAID, "Contracted"];
// Won: the client said yes, whatever has happened since (for conversion).
export const WON_STATUSES = ["Accepted", DEPOSIT_PENDING, DEPOSIT_PAID, "Contracted", "Invoiced", "Complete"];
export const isLostStatus = s => s === LOST || s === "Rejected";

export const isAutomotive = q => q?.["Category"] === "Automotive";

// Manual status changes the panel offers. Draft/Amended → Sent happens by
// sending; Accepted → Contracted only via the GoodSign webhook (Industrial,
// Marine). Automotive has no contract: Accepted → Deposit pending (invoice
// sent) → Deposit paid → Complete (balance paid on collection).
export function manualTransitions(q) {
  const auto = isAutomotive(q);
  switch (q?.["Status"] || "Draft") {
    case "Sent": return ["Accepted", LOST];
    case "Accepted": return auto ? [DEPOSIT_PENDING, LOST] : [LOST];
    case DEPOSIT_PENDING: return [DEPOSIT_PAID, LOST];
    case DEPOSIT_PAID: return ["Complete"];
    case "Contracted": return ["Invoiced"];
    case "Invoiced": return ["Complete"];
    default: return [];
  }
}

export const LOSS_REASONS = ["Price", "No response", "Went elsewhere", "Timing", "Didn't need it", "Other"];
export const REASON_NOT_RECORDED = "Reason not recorded";

// A quote's loss reason ("" if it isn't lost). Old Rejected rows have none.
export function lossReasonOf(q) {
  if (!isLostStatus(q?.["Status"])) return "";
  return String(q["Loss Reason"] || "").trim() || REASON_NOT_RECORDED;
}

// Current versions of real (non-test) quotes.
const realCurrent = quotes => (quotes || []).filter(q => q["Is Current"] !== "No" && !isTestQuote(q));

// Dashboard: won ÷ (won + lost) over current real quotes, and how many were
// lost for each reason (most common first).
export function conversionStats(quotes) {
  const cur = realCurrent(quotes);
  const won = cur.filter(q => WON_STATUSES.includes(q["Status"])).length;
  const lostQs = cur.filter(q => isLostStatus(q["Status"]));
  const decided = won + lostQs.length;
  const counts = {};
  lostQs.forEach(q => { const r = lossReasonOf(q); counts[r] = (counts[r] || 0) + 1; });
  const reasons = Object.entries(counts).map(([reason, count]) => ({ reason, count })).sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
  return { won, lost: lostQs.length, rate: decided ? won / decided : null, reasons };
}

// The newest History note for a quote: { text, toStatus, timestamp } or null.
// Same-status lines (contact edits, document notes) count too.
export function latestStatusNote(statusLog, quoteNumber) {
  let last = null;
  (statusLog || []).forEach(e => {
    if (e.quoteNumber !== quoteNumber || !String(e.reason || "").trim()) return;
    if (!last || String(e.timestamp) > String(last.timestamp)) last = e;
  });
  return last ? { text: last.reason.trim(), toStatus: last.toStatus, timestamp: last.timestamp } : null;
}

// Booked jobs (Accepted or later) whose days overlap start..end, other than
// this quote's own versions. Test quotes are only checked against test
// quotes, so trial jobs don't warn about real ones and vice versa.
// start/end: "YYYY-MM-DD" (end "" = one day).
export function overlappingJobs({ quoteNumber, start, end, test }, quotes) {
  if (!start) return [];
  const last = end && end >= start ? end : start;
  const base = String(quoteNumber || "").replace(/[a-z]+$/, "");
  return (quotes || []).filter(q => {
    if (q["Is Current"] === "No" || !JOB_STATUSES.includes(q["Status"])) return false;
    if (base && String(q["Quote Number"] || "").replace(/[a-z]+$/, "") === base) return false;
    if (!!isTestQuote(q) !== !!test) return false;
    const d = getJobDates(q);
    return d.start && d.start <= last && d.end >= start;
  });
}
